import { niceMax, tierColor } from '../lib/palette';
import { formatBytes, type StorageEstimate } from '../lib/storage';

const W = 560;
const H = 260;
const M = { top: 14, right: 16, bottom: 34, left: 74 };

interface Props {
  estimate: StorageEstimate;
}

/**
 * Resident bytes over time under both designs. Both curves rise while buckets fill
 * and flatten once each retention window is saturated — the gap between the plateaus
 * is the steady-state saving.
 */
export function GrowthChart({ estimate }: Props) {
  const points = estimate.growth;
  const maxY = niceMax(Math.max(...points.map((p) => Math.max(p.rawOnly, p.tiered)), 1));
  const maxX = estimate.horizonDays;
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const x = (day: number) => M.left + (day / maxX) * plotW;
  const y = (bytes: number) => M.top + plotH - (bytes / maxY) * plotH;

  const path = (key: 'rawOnly' | 'tiered') =>
    points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.day).toFixed(1)} ${y(p[key]).toFixed(1)}`)
      .join(' ');

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxY);
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * maxX));

  return (
    <figure style={{ margin: 0 }}>
      <figcaption className="panel-note" style={{ marginBottom: 8 }}>
        Resident storage over time
      </figcaption>
      <svg
        className="chart"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        data-testid="growth-chart"
        aria-label={`Line chart of resident storage over ${maxX} days. Keeping raw data for the whole period reaches ${formatBytes(
          estimate.rawOnlyBytes,
        )}; the tiered design settles at ${formatBytes(estimate.tieredBytes)}.`}
      >
        {yTicks.map((t) => (
          <g key={t}>
            <line
              x1={M.left}
              x2={W - M.right}
              y1={y(t)}
              y2={y(t)}
              stroke="var(--border)"
              strokeWidth={1}
            />
            <text x={M.left - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--text-dim)">
              {formatBytes(t)}
            </text>
          </g>
        ))}
        {xTicks.map((t) => (
          <text
            key={t}
            x={x(t)}
            y={H - 12}
            textAnchor="middle"
            fontSize={11}
            fill="var(--text-dim)"
          >
            {t}d
          </text>
        ))}
        <path d={path('rawOnly')} fill="none" stroke="var(--chart-raw)" strokeWidth={2.5} />
        <path d={path('tiered')} fill="none" stroke="var(--chart-tiered)" strokeWidth={2.5} />
      </svg>
      <ul className="legend">
        <li>
          <span className="swatch" style={{ background: 'var(--chart-raw)' }} /> raw kept for the
          whole horizon
        </li>
        <li>
          <span className="swatch" style={{ background: 'var(--chart-tiered)' }} /> tiered design
        </li>
      </ul>
    </figure>
  );
}

/** Resident volume per bucket, as a bar per level of the cascade. */
export function BreakdownChart({ estimate }: Props) {
  const levels = estimate.levels;
  const rowH = 34;
  const labelW = 168;
  const width = 600;
  const height = levels.length * rowH + 26;
  const max = Math.max(...levels.map((l) => l.steadyStateBytes), 1);
  const barMax = width - labelW - 88;

  return (
    <figure style={{ margin: 0 }}>
      <figcaption className="panel-note" style={{ marginBottom: 8 }}>
        Steady-state volume per bucket
      </figcaption>
      <svg
        className="chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        data-testid="breakdown-chart"
        aria-label={`Bar chart of resident volume per bucket: ${levels
          .map((l) => `${l.bucket} ${formatBytes(l.steadyStateBytes)}`)
          .join(', ')}.`}
      >
        {levels.map((level, i) => {
          const y = i * rowH + 6;
          const w = Math.max(2, (level.steadyStateBytes / max) * barMax);
          return (
            <g key={level.bucket + i}>
              <text
                x={0}
                y={y + 17}
                fontSize={12}
                fill="var(--text)"
                fontFamily="ui-monospace, Menlo, Consolas, monospace"
              >
                {level.bucket.length > 22 ? `${level.bucket.slice(0, 21)}…` : level.bucket}
              </text>
              <rect
                x={labelW}
                y={y + 4}
                width={w}
                height={18}
                rx={4}
                fill={i === 0 ? 'var(--chart-raw)' : tierColor(i - 1)}
              />
              <text x={labelW + w + 8} y={y + 18} fontSize={12} fill="var(--text-dim)">
                {formatBytes(level.steadyStateBytes)}
              </text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
}
